import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  IconHome,
  IconClipboardList,
  IconMessageCircle,
  IconNotes,
  IconUsers,
  IconFolder,
  IconSearch,
  IconMovie,
  IconBuildingStore,
  IconBuilding,
  IconShoppingBag,
  IconPackage,
} from '@tabler/icons-react';
import { useMingo } from '../context/MingoContext';
import { TabLineIcon } from './icons/TabLineIcons';
import UserFabSheet from './UserFabSheet';
import { resolveUserFabActions, shouldHideUserFab } from '../config/userFabRegistry';
import MingoEgg from './MingoEgg';
import { MINGO_RETURN_PATH_KEY, withReturnState } from '../lib/goBack';

const TAB_INACTIVE = '#bdbdbd';
const TAB_ACTIVE = '#7c3aed';

const NAV_TABS = [
  { id: 'home', path: 'home', labelKey: 'tabs.home', icon: 'home', flag: null },
  { id: 'story', path: 'shorts', labelKey: 'tabs.story', icon: 'story', flag: 'trade' },
  { id: 'community', path: 'community', labelKey: 'tabs.community', icon: 'community', flag: 'community' },
  { id: 'profile', path: 'friends', labelKey: 'tabs.profile', icon: 'user', flag: null },
];

const ACTION_ICONS = {
  property_write: IconHome,
  my_properties: IconClipboardList,
  community_write: IconMessageCircle,
  my_community_posts: IconNotes,
  group_create: IconUsers,
  my_groups: IconFolder,
  search_groups: IconSearch,
  story_write: IconMovie,
  business_register: IconBuildingStore,
  my_businesses: IconBuilding,
  product_write: IconShoppingBag,
  my_trades: IconPackage,
  search: IconSearch,
};

function BottomTab() {
  const { isFeatureEnabled, t, isGuest, openLoginRequired } = useMingo();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [fabOpen, setFabOpen] = useState(false);
  const [fabHatching, setFabHatching] = useState(false);

  const visibleTabs = useMemo(
    () => NAV_TABS.filter((tab) => (!tab.flag ? true : isFeatureEnabled(tab.flag))),
    [isFeatureEnabled],
  );

  const rawActions = useMemo(
    () => resolveUserFabActions({ pathname, search, isFeatureEnabled }),
    [pathname, search, isFeatureEnabled],
  );

  const fabActions = useMemo(
    () =>
      rawActions.map((a) => {
        const Icon = ACTION_ICONS[a.id];
        return {
          ...a,
          label: t(a.labelKey),
          icon: Icon ? <Icon size={20} stroke={2} aria-hidden /> : '',
        };
      }),
    [rawActions, t],
  );

  const fabSheetVariant = fabActions.length > 1 ? 'circle' : 'list';

  const hideFab = shouldHideUserFab(pathname);

  const tabReturnState = useMemo(() => {
    const from = `${pathname}${search || ''}`;
    if (!from.startsWith('/') || from.startsWith('/shorts')) return undefined;
    return { [MINGO_RETURN_PATH_KEY]: from };
  }, [pathname, search]);

  const runFabAction = useCallback(
    (actionId) => {
      const action = rawActions.find((a) => a.id === actionId);
      if (!action?.to) return;
      setFabOpen(false);
      const go = () => navigate(action.to, { state: withReturnState({ pathname, search }) });
      if (action.requireAuth && isGuest) {
        openLoginRequired(go);
        return;
      }
      go();
    },
    [rawActions, isGuest, navigate, openLoginRequired, pathname, search],
  );

  const onFabClick = useCallback(() => {
    if (!fabActions.length) return;
    setFabHatching(true);
    window.setTimeout(() => setFabHatching(false), 560);
    if (fabActions.length === 1) {
      runFabAction(fabActions[0].id);
      return;
    }
    setFabOpen((v) => !v);
  }, [fabActions, runFabAction]);

  useEffect(() => {
    setFabOpen(false);
  }, [pathname, search]);

  if (hideFab) return null;

  const leftTabs = visibleTabs.slice(0, 2);
  const rightTabs = visibleTabs.slice(2);

  return (
    <>
      <nav className="mingo-bottom-tab mingo-bottom-tab--notch" role="navigation" aria-label={t('common.tabBar')}>
        <div className="mingo-bottom-tab__shell">
          <svg className="mingo-bottom-tab__notch" viewBox="0 0 375 58" preserveAspectRatio="none" aria-hidden="true">
            <path
              d="M0 8h120c8 0 14 6 14 14v0c0 22 18 40 40 40h27c22 0 40-18 40-40v0c0-8 6-14 14-14h120v50H0V8z"
              fill="#fff"
            />
          </svg>
          <ul className="mingo-bottom-tab__list">
            {leftTabs.map((tab) => (
              <li key={tab.id} className="mingo-bottom-tab__item">
                <NavLink
                  to={`/${tab.path}`}
                  end={tab.path === 'home'}
                  replace
                  state={tab.id === 'story' ? tabReturnState : undefined}
                  className={({ isActive: navActive }) =>
                    `mingo-bottom-tab__btn${navActive ? ' mingo-bottom-tab__btn--active' : ''}`
                  }
                >
                  {({ isActive: navActive }) => (
                    <>
                      <span className="mingo-bottom-tab__icon-wrap">
                        <span
                          className="mingo-bottom-tab__icon"
                          aria-hidden="true"
                          style={{ color: navActive ? TAB_ACTIVE : TAB_INACTIVE }}
                        >
                          <TabLineIcon name={tab.icon} active={navActive} color={navActive ? TAB_ACTIVE : TAB_INACTIVE} />
                        </span>
                      </span>
                      <span className="mingo-bottom-tab__label" style={{ color: navActive ? TAB_ACTIVE : TAB_INACTIVE }}>
                        {t(tab.labelKey)}
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
            <li className="mingo-bottom-tab__item mingo-bottom-tab__item--center">
              <button
                type="button"
                className="mingo-bottom-tab__btn mingo-bottom-tab__btn--center"
                aria-label={fabOpen ? t('userFab.close') : t('userFab.create')}
                aria-expanded={fabOpen}
                onClick={onFabClick}
                disabled={!fabActions.length}
                style={{ opacity: fabActions.length ? 1 : 0.45 }}
              >
                <span className="mingo-bottom-tab__fab-ring">
                  <span className="mingo-bottom-tab__fab-btn">
                    <MingoEgg hatching={fabHatching} open={fabOpen} />
                  </span>
                </span>
              </button>
            </li>
            {rightTabs.map((tab) => (
              <li key={tab.id} className="mingo-bottom-tab__item">
                <NavLink
                  to={`/${tab.path}`}
                  replace
                  className={({ isActive: navActive }) =>
                    `mingo-bottom-tab__btn${navActive ? ' mingo-bottom-tab__btn--active' : ''}`
                  }
                >
                  {({ isActive: navActive }) => (
                    <>
                      <span className="mingo-bottom-tab__icon-wrap">
                        <span
                          className="mingo-bottom-tab__icon"
                          aria-hidden="true"
                          style={{ color: navActive ? TAB_ACTIVE : TAB_INACTIVE }}
                        >
                          <TabLineIcon
                            name={tab.icon}
                            active={navActive}
                            color={navActive ? TAB_ACTIVE : TAB_INACTIVE}
                          />
                        </span>
                      </span>
                      <span
                        className="mingo-bottom-tab__label"
                        style={{ color: navActive ? TAB_ACTIVE : TAB_INACTIVE }}
                      >
                        {t(tab.labelKey)}
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>
      <UserFabSheet
        open={fabOpen}
        onClose={() => setFabOpen(false)}
        actions={fabActions}
        onSelect={runFabAction}
        title={t('userFab.sheetTitle')}
        variant={fabSheetVariant}
      />
    </>
  );
}

export default BottomTab;
